import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import {
  createFunnelAuditPublicLink, downloadFunnelAuditsCsv, getFunnelAuditBrands, getFunnelAudits,
  type FunnelAuditListParams,
} from '@/api/funnel-audits';
import { auditOfferLabel, formatDate, formatFlag, formatMinor } from '@/lib/funnel-audits';
import { COUNTRIES } from '@/lib/countries';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';

const TH = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-content-subtle whitespace-nowrap';
const TD = 'px-4 py-3 text-sm text-content-muted';

export function FunnelAudits() {
  const canEdit = useAuthStore((state) => state.canEdit());
  const pushToast = useToastStore((state) => state.push);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [brandId, setBrandId] = useState('');
  const [geo, setGeo] = useState('');
  const [latestOnly, setLatestOnly] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [publicLink, setPublicLink] = useState('');

  const createPublicLink = useMutation({
    mutationFn: createFunnelAuditPublicLink,
    onSuccess: async (link) => {
      const url = `${window.location.origin}/funnel-audits/new/${link.token}`;
      setPublicLink(url);
      try {
        await navigator.clipboard.writeText(url);
        pushToast('A unique public form link was created and copied.');
      } catch {
        pushToast('A unique public form link was created. Copy it from the field below.');
      }
    },
    onError: (error: Error) => pushToast(error.message || 'Failed to create public form link'),
  });

  const params = useMemo<FunnelAuditListParams>(() => ({
    page, per_page: 50, search: search.trim(), brand_id: brandId,
    geo: geo.trim().toUpperCase(), latest_only: latestOnly,
  }), [page, search, brandId, geo, latestOnly]);

  const brands = useQuery({ queryKey: ['funnel-audit-brands'], queryFn: getFunnelAuditBrands });
  const audits = useQuery({
    queryKey: ['funnel-audits', params],
    queryFn: () => getFunnelAudits(params),
  });
  const pages = Math.max(1, Math.ceil((audits.data?.total ?? 0) / (audits.data?.per_page ?? 50)));

  function resetPage(action: () => void) {
    action();
    setPage(1);
  }

  async function exportCsv() {
    setExporting(true);
    try {
      await downloadFunnelAuditsCsv(params);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold text-content">Funnel Audits</h1>
          <p className="mt-1 text-sm text-content-subtle">Brand-first product and registration funnel checks. Offers can be connected later.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" loading={exporting} onClick={() => void exportCsv()}>Export CSV</Button>
          {canEdit && <Button variant="secondary" loading={createPublicLink.isPending} onClick={() => createPublicLink.mutate()}>Create & copy public link</Button>}
          {canEdit && <Link to="/funnel-audits/new"><Button>New audit</Button></Link>}
        </div>
      </div>

      {publicLink && (
        <div className="card flex flex-col gap-2 p-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-content">Public form link</div>
            <input className="input mt-1 w-full" readOnly value={publicLink} onFocus={(event) => event.currentTarget.select()} />
          </div>
          <Button variant="secondary" onClick={() => void navigator.clipboard.writeText(publicLink).then(
            () => pushToast('Public form link copied.'),
            () => pushToast('Select and copy the link manually.'),
          )}>Copy</Button>
        </div>
      )}

      <div className="card p-4 grid gap-3 md:grid-cols-4">
        <input
          value={search}
          onChange={(event) => resetPage(() => setSearch(event.target.value))}
          placeholder="Search brand, partner, URL or notes"
          className="input md:col-span-2"
        />
        <Select
          value={brandId}
          onChange={(value) => resetPage(() => setBrandId(value))}
          options={[
            { value: '', label: 'All brands' },
            ...(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
          ]}
          placeholder="All brands"
          searchable
          className="w-full"
        />
        <Select
          value={geo}
          onChange={(value) => resetPage(() => setGeo(value))}
          options={[
            { value: '', label: 'All GEOs' },
            ...COUNTRIES.map((country) => ({
              value: country.code,
              label: `${country.flag} ${country.name} (${country.code})`,
            })),
          ]}
          placeholder="All GEOs"
          searchable
          className="w-full"
        />
        <label className="md:col-span-4 flex items-center gap-2 text-sm text-content-muted">
          <input
            type="checkbox"
            checked={latestOnly}
            onChange={(event) => resetPage(() => setLatestOnly(event.target.checked))}
            className="rounded border-border-strong text-primary focus:ring-primary"
          />
          Show only the latest audit for each brand × GEO
        </label>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full min-w-[1050px]">
          <thead className="bg-surface-muted border-b border-border">
            <tr>
              <th className={TH}>Brand</th><th className={TH}>GEO</th><th className={TH}>Min. deposit</th>
              <th className={TH}>Registration</th><th className={TH}>One click</th><th className={TH}>Crash games</th>
              <th className={TH}>Screenshots</th><th className={TH}>Author</th><th className={TH}>Checked</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {audits.isLoading && <tr><td colSpan={9} className="px-4 py-12 text-center text-content-subtle">Loading audits…</td></tr>}
            {audits.isError && <tr><td colSpan={9} className="px-4 py-12 text-center text-danger">Failed to load audits.</td></tr>}
            {!audits.isLoading && audits.data?.items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-content-subtle">No audits match these filters.</td></tr>
            )}
            {audits.data?.items.map((audit) => (
              <tr key={audit.id} className="hover:bg-surface-muted/50">
                <td className={TD}>
                  <Link to={`/funnel-audits/${audit.id}`} className="font-medium text-primary hover:underline">
                    {auditOfferLabel(audit)}
                  </Link>
                  <div className="mt-0.5 text-xs text-content-subtle">{audit.offer_id ? `Offer · ${audit.offer_partner}` : 'No offer connected'}</div>
                </td>
                <td className={`${TD} font-medium text-content`}>{audit.geos.join(', ')}</td>
                <td className={`${TD} tabular-nums`}>${formatMinor(audit.min_deposit_usd)}</td>
                <td className={TD}>{audit.reg_complexity_score ? `${audit.reg_complexity_score}/5` : '—'}</td>
                <td className={TD}>{formatFlag(audit.has_instant_one_click)}</td>
                <td className={TD}>{formatFlag(audit.has_crash_games)}</td>
                <td className={TD}>{audit.screenshot_count ?? 0}</td>
                <td className={TD}>{audit.created_by_name}</td>
                <td className={`${TD} whitespace-nowrap`}>{formatDate(audit.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-content-muted">
        <span>{audits.data?.total ?? 0} audits</span>
        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
          <span>Page {page} of {pages}</span>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((value) => value + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
